import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { APIClient } from "../../helpers/api_helper";
import * as url from "../../helpers/url_helper";

const api = new APIClient();

/**
 * Fetch user's current plan
 */
export const fetchUserPlan = createAsyncThunk("plans/fetchUserPlan", async (_, { rejectWithValue }) => {
  try {
    const response = await api.get(`${url.GET_USER_PROFILE}`);
    return response.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

/**
 * Upgrade user's plan
 */
export const upgradePlan = createAsyncThunk("plans/upgradePlan", async (planId, { rejectWithValue }) => {
  try {
    const response = await api.put("/user/plan", { plan: planId });
    return response.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

/**
 * Initiate payment for plan upgrade
 */
export const initiatePayment = createAsyncThunk(
  "plans/initiatePayment",
  async ({ planId, billingCycle }, { rejectWithValue }) => {
    try {
      const response = await api.post("/payment/initiate", {
        planId,
        billingCycle, // "monthly" or "yearly"
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const initialState = {
  userPlan: null,
  plans: {
    free: {
      id: "free",
      name: "FREE",
      price: 0,
      limits: { customers: 15, reminders: 30, templates: 5, channels: ["email"] },
    },
    basic: {
      id: "basic",
      name: "BASIC",
      price: 99,
      limits: { customers: 50, reminders: 100, templates: 10, channels: ["email", "whatsapp"] },
    },
    pro: {
      id: "pro",
      name: "PRO",
      price: 149,
      limits: { customers: null, reminders: null, templates: null, channels: ["email", "whatsapp", "call"] },
    },
  },
  loading: false,
  error: null,
  success: null,
};

const plansSlice = createSlice({
  name: "plans",
  initialState,
  reducers: {
    resetPlanError: (state) => {
      state.error = null;
    },
    resetPlanSuccess: (state) => {
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch User Plan
      .addCase(fetchUserPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.userPlan = action.payload.plan || "free";
      })
      .addCase(fetchUserPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Upgrade Plan
      .addCase(upgradePlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(upgradePlan.fulfilled, (state, action) => {
        state.loading = false;
        state.userPlan = action.payload.plan;
        state.success = "Plan upgraded successfully!";
      })
      .addCase(upgradePlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Initiate Payment
      .addCase(initiatePayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initiatePayment.fulfilled, (state, action) => {
        state.loading = false;
        state.success = "Payment initiated. Redirecting to payment gateway...";
      })
      .addCase(initiatePayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetPlanError, resetPlanSuccess } = plansSlice.actions;
export default plansSlice.reducer;
